/**
 * Dynamic Lighting Engine for the VTT.
 *
 * Pure functions — no React state, no canvas, no DOM. Given a vision origin,
 * a radius, and a set of wall segments, returns an ordered polygon describing
 * exactly what that origin can see.
 *
 * Algorithm: shadow-casting via raycasting at every wall endpoint (plus two
 * grazing rays a hair to either side of each endpoint, to catch the case
 * where a wall blocks the ray exactly at its corner). Each ray is clipped
 * against the nearest wall it hits, or against the vision radius if no wall
 * is in range. Hit points are sorted by angle to form a clean polygon.
 *
 * Doors that are `open: true` are skipped — they pass light through.
 *
 * Performance: O(W*W) per source. For typical D&D maps (~50 walls, ~5 light
 * sources) that's a few thousand ops per redraw — fast enough on canvas/SVG
 * at 60fps. If campaigns grow much beyond that, the next step is to spatially
 * index the walls.
 */

const TAU = Math.PI * 2;
const EPS = 0.00005;

// Active walls = solid walls + closed doors + shuttered windows.
// Doors default closed (blocking); toggling sets `open: true` so light passes.
// Windows default open (light passes); toggling sets `open: false` so light blocks.
function activeWalls(walls) {
  return (walls || []).filter(w => {
    if (w.kind === "door"   && w.open === true)  return false;
    if (w.kind === "window" && w.open !== false) return false;
    return true;
  });
}

// Ray-segment intersection in 2D. Returns the parametric t along the ray
// (in world units, since the ray direction is a unit vector) for the nearest
// hit, or null if the ray misses or the wall is behind the origin.
function rayHit(ox, oy, dx, dy, ax, ay, bx, by) {
  const r_px = ox;
  const r_py = oy;
  const r_dx = dx;
  const r_dy = dy;
  const s_px = ax;
  const s_py = ay;
  const s_dx = bx - ax;
  const s_dy = by - ay;
  const denom = r_dx * s_dy - r_dy * s_dx;
  if (Math.abs(denom) < EPS) return null; // parallel
  const t = ((s_px - r_px) * s_dy - (s_py - r_py) * s_dx) / denom;
  const u = ((s_px - r_px) * r_dy - (s_py - r_py) * r_dx) / denom;
  if (t < 0 || u < 0 || u > 1) return null;
  return t;
}

/**
 * Compute the visibility polygon for a single source.
 *
 * @param {number} ox  Origin x (world space).
 * @param {number} oy  Origin y (world space).
 * @param {number} radius  Vision radius in world units.
 * @param {Array}  walls   Wall segments [{x1,y1,x2,y2,kind,open}, ...].
 * @returns {Array<[number,number]>}  Polygon vertices in CCW order.
 */
export function computeVisibilityPolygon(ox, oy, radius, walls) {
  const segs = activeWalls(walls);

  // Build the list of angles we want to shoot rays along.
  const angles = [];
  for (const w of segs) {
    const a1 = Math.atan2(w.y1 - oy, w.x1 - ox);
    const a2 = Math.atan2(w.y2 - oy, w.x2 - ox);
    // Grazing rays just past each corner so we wrap cleanly behind walls.
    angles.push(a1 - 0.00015, a1, a1 + 0.00015);
    angles.push(a2 - 0.00015, a2, a2 + 0.00015);
  }
  // Always include enough rays to draw a smooth circle when there are no
  // walls (or the source is in open ground).
  const RING_SAMPLES = 60;
  for (let i = 0; i < RING_SAMPLES; i++) {
    angles.push((i / RING_SAMPLES) * TAU - Math.PI);
  }
  angles.sort((a, b) => a - b);

  const points = [];
  for (const a of angles) {
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    let nearest = radius;
    for (const w of segs) {
      const t = rayHit(ox, oy, dx, dy, w.x1, w.y1, w.x2, w.y2);
      if (t !== null && t < nearest) nearest = t;
    }
    points.push([ox + dx * nearest, oy + dy * nearest]);
  }
  return points;
}

/**
 * Segment-vs-segment intersection test. Returns the intersection point if the
 * two segments cross, or null otherwise. Used by `segmentBlockedByWall` for
 * token-movement collision.
 *
 * (ax, ay) → (bx, by) is segment A (the proposed token path).
 * (cx, cy) → (dx, dy) is segment B (a wall).
 */
function segmentIntersect(ax, ay, bx, by, cx, cy, dx, dy) {
  const rpx = bx - ax;
  const rpy = by - ay;
  const spx = dx - cx;
  const spy = dy - cy;
  const denom = rpx * spy - rpy * spx;
  if (Math.abs(denom) < EPS) return null;
  const t = ((cx - ax) * spy - (cy - ay) * spx) / denom;
  const u = ((cx - ax) * rpy - (cy - ay) * rpx) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { x: ax + t * rpx, y: ay + t * rpy, t };
}

/**
 * Returns the closest blocking-wall hit along the segment from (x1,y1) to
 * (x2,y2), or null if the path is clear. Open doors and open windows are
 * skipped (tokens can pass through them).
 *
 * The hit point is pulled back slightly along the path so the caller can
 * place the token just before the wall without z-fighting.
 *
 * @returns {null | { x: number, y: number, wall: object, t: number }}
 */
export function segmentBlockedByWall(x1, y1, x2, y2, walls) {
  const segs = activeWalls(walls);
  if (segs.length === 0) return null;
  let bestT = Infinity;
  let bestHit = null;
  for (const w of segs) {
    const hit = segmentIntersect(x1, y1, x2, y2, w.x1, w.y1, w.x2, w.y2);
    if (hit && hit.t < bestT) {
      bestT = hit.t;
      bestHit = { x: hit.x, y: hit.y, wall: w, t: hit.t };
    }
  }
  if (!bestHit) return null;
  // Pull back ~2 world-pixels so the token doesn't end exactly on the wall.
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const pullback = Math.min(2, bestT * len * 0.95);
  const ux = dx / len, uy = dy / len;
  return {
    x: bestHit.x - ux * pullback,
    y: bestHit.y - uy * pullback,
    wall: bestHit.wall,
    t: bestT,
  };
}

/**
 * Convert a polygon (array of [x,y]) to an SVG path "d" string.
 */
export function polygonToPath(poly) {
  if (!poly || poly.length === 0) return "";
  let d = `M ${poly[0][0].toFixed(2)} ${poly[0][1].toFixed(2)}`;
  for (let i = 1; i < poly.length; i++) {
    d += ` L ${poly[i][0].toFixed(2)} ${poly[i][1].toFixed(2)}`;
  }
  d += " Z";
  return d;
}

/**
 * Convenience: compute visibility for a list of sources and return an array
 * of SVG path strings.
 *
 * Each source = {x, y, radius}.
 */
export function computeVisibilityPaths(sources, walls) {
  return (sources || []).map(s => polygonToPath(computeVisibilityPolygon(s.x, s.y, s.radius, walls)));
}

/**
 * Vision sources for tokens. Each token contributes up to one entry, using
 * the largest of its bright / dim / dark vision radii (in feet, converted
 * to world pixels using `pxPerFt`).
 *
 * Tokens with 0 vision (NPCs whose POV doesn't matter to the players) are
 * excluded.
 *
 * Returns: [{ id, x, y, radius, bright, dim, dark }]
 */
export function tokenVisionSources(tokens, pxPerFt) {
  const out = [];
  for (const t of (tokens || [])) {
    const bright = Number(t.vision_bright ?? 0);
    const dim    = Number(t.vision_dim ?? 0);
    const dark   = Number(t.vision_dark ?? 0);
    const legacy = Number(t.vision_radius ?? 0); // back-compat with phase-2 simple sight
    const ft = Math.max(bright + dim, dark, legacy);
    if (ft <= 0) continue;
    const tsz = (t.size || 1);
    const cx = t.x + tsz * pxPerFt * 5 / 2;  // token centre (size is in grid squares; 1 square = 5ft)
    const cy = t.y + tsz * pxPerFt * 5 / 2;
    out.push({
      id: `tok-${t.id}`,
      x: cx, y: cy,
      radius: ft * pxPerFt,
      bright: bright * pxPerFt,
      dim:    dim    * pxPerFt,
      dark:   dark   * pxPerFt,
    });
  }
  return out;
}

/**
 * Light-emitter sources from placed assets. Each asset that has a
 * `light_bright` value > 0 contributes a light source at its centre.
 */
export function assetLightSources(assets, pxPerFt) {
  const out = [];
  for (const a of (assets || [])) {
    const bright = Number(a.light_bright ?? 0);
    const dim    = Number(a.light_dim ?? 0);
    const ft = bright + dim;
    if (ft <= 0) continue;
    const w = (a.width  ?? a.size ?? 1);
    const h = (a.height ?? a.size ?? 1);
    const cx = a.x + (w * pxPerFt * 5) / 2;
    const cy = a.y + (h * pxPerFt * 5) / 2;
    out.push({
      id: `lit-${a.id}`,
      x: cx, y: cy,
      radius: ft * pxPerFt,
      bright: bright * pxPerFt,
      dim:    dim    * pxPerFt,
    });
  }
  return out;
}
